import { Injectable } from '@angular/core';
import { ExternalData } from './external-data.model';

/**
 * Map of old Aurelia hash routes to new form parameter values.
 */
const LEGACY_ROUTE_TO_FORM: Record<string, string> = {
  '':                     'payment',
  'payment-form':         'payment',
  'pan-token-generation': 'pan-token-generation',
};

/**
 * Captured at module load, before Angular's router clears the hash.
 * Old integrations set the iframe src to e.g. "/#/pan-token-generation",
 * but Angular's PathLocationStrategy removes the hash on first navigation.
 */
const INITIAL_HASH = window.location.hash;

/**
 * Handles backwards compatibility with the old Aurelia e-commerce UI.
 *
 * Old integrations may differ in up to five ways:
 * 1. No `form` field — routing was done via URL hash (e.g. /#/pan-token-generation)
 * 2. `hostApp` instead of `stylesheetName` for theming (mapped with `legacy_` prefix)
 * 3. `useNewCardTokenForm` instead of `useShortCardTokenForm` / `use3DSCardTokenForm`
 *    (`useNewCardTokenForm: true` → 3DS form; absent/false → V2 extended form default)
 * 4. No `placeholders` field — old app always showed placeholders (default: true)
 * 5. No `permissiveSubmitButton` field — old app never disabled submit (default: true)
 *
 * Each field is normalized independently so that partially migrated
 * integrations (e.g. already sending `form` but still using `hostApp`)
 * work correctly.
 */
@Injectable({
  providedIn: 'root',
})
export class LegacyDataNormalizerService {
  /** Defaults to the hash captured at module load; overridable in tests. */
  initialHash = INITIAL_HASH;

  normalize(data: any): ExternalData {
    if (!this.needsNormalization(data)) {
      return data;
    }

    const normalized = { ...data };

    // Infer `form` from the URL hash captured at load time (old integrations
    // set iframe src to e.g. "/#/pan-token-generation"). Fall back to "payment".
    if (!data.form) {
      const hashRoute = this.initialHash.replace(/^#\/?/, '');
      normalized.form = LEGACY_ROUTE_TO_FORM[hashRoute] ?? 'payment';
    }

    // Map hostApp → stylesheetName with legacy_ prefix so legacy skins
    // are namespaced separately and unavailable to new integrations.
    // Legacy apps without hostApp get legacy_default.
    if (!data.stylesheetName) {
      normalized.stylesheetName = data.hostApp ? 'legacy_' + data.hostApp : 'legacy_default';
    }

    // Map useNewCardTokenForm → useShortCardTokenForm / use3DSCardTokenForm.
    // V2 default (absent flags): extended form (PAN + expiry).
    // useNewCardTokenForm: true → 3DS form (PAN + expiry + name).
    // PAN-only requires explicit useShortCardTokenForm: true.
    if (normalized.useShortCardTokenForm === undefined && normalized.use3DSCardTokenForm === undefined) {
      if (data.useNewCardTokenForm) {
        normalized.use3DSCardTokenForm = true;
        normalized.useShortCardTokenForm = false;
      } else {
        normalized.useShortCardTokenForm = false;
        normalized.use3DSCardTokenForm = false;
      }
    }

    if (normalized.placeholders === undefined) {
      normalized.placeholders = true;
    }

    if (normalized.permissiveSubmitButton === undefined) {
      normalized.permissiveSubmitButton = true;
    }

    return normalized;
  }

  private needsNormalization(data: any): boolean {
    if (!data.form) return true;
    if (data.hostApp && !data.stylesheetName) return true;
    if (data.useShortCardTokenForm === undefined && data.use3DSCardTokenForm === undefined) return true;
    if (data.placeholders === undefined) return true;
    if (data.permissiveSubmitButton === undefined) return true;
    return false;
  }
}
